import type { RefObject } from 'react';
import { useCallback, useEffect, useState } from 'react';

import {
  exitDocumentFullscreen,
  getFullscreenElement,
  isElementInFullscreen,
  isFullscreenSupported,
  requestElementFullscreen,
} from '../../VideoUpload.utils';

const fullscreenEvents = [
  'fullscreenchange',
  'webkitfullscreenchange',
  'mozfullscreenchange',
  'MSFullscreenChange',
];

export const useFullscreen = (
  videoPlayerContainerRef: RefObject<HTMLDivElement>,
  videoRef: RefObject<HTMLVideoElement>,
) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isFullscreenEnabled, setIsFullscreenEnabled] = useState(false);

  const enterFullscreen = useCallback(async () => {
    const videoPlayerContainerElement = videoPlayerContainerRef.current;
    const videoElement = videoRef.current;
    if (!videoPlayerContainerElement || !videoElement) {
      return;
    }

    const fullscreenElement = getFullscreenElement(
      videoPlayerContainerElement,
      videoElement,
    );
    await requestElementFullscreen(fullscreenElement);
  }, [videoPlayerContainerRef, videoRef]);

  const exitFullscreen = useCallback(async () => {
    const videoPlayerContainerElement = videoPlayerContainerRef.current;
    const videoElement = videoRef.current;
    if (!videoPlayerContainerElement || !videoElement) {
      return;
    }

    const fullscreenElement = getFullscreenElement(
      videoPlayerContainerElement,
      videoElement,
    );
    await exitDocumentFullscreen(fullscreenElement);
  }, [videoPlayerContainerRef, videoRef]);

  const toggleFullscreen = useCallback(() => {
    if (isFullscreen) {
      return exitFullscreen();
    } else {
      return enterFullscreen();
    }
  }, [enterFullscreen, exitFullscreen, isFullscreen]);

  useEffect(() => {
    const videoPlayerContainerElement = videoPlayerContainerRef.current;
    const videoElement = videoRef.current;
    if (!videoPlayerContainerElement || !videoElement) {
      setIsFullscreenEnabled(false);
      setIsFullscreen(false);
      return;
    }

    const fullscreenElement = getFullscreenElement(
      videoPlayerContainerElement,
      videoElement,
    );
    setIsFullscreenEnabled(isFullscreenSupported(fullscreenElement));

    const handleFullscreenChange = () => {
      setIsFullscreen(isElementInFullscreen(fullscreenElement));
    };

    fullscreenEvents.forEach(eventName => {
      document.addEventListener(eventName, handleFullscreenChange);
    });

    return () => {
      fullscreenEvents.forEach(eventName => {
        document.removeEventListener(eventName, handleFullscreenChange);
      });
    };
  }, [videoPlayerContainerRef, videoRef]);

  return {
    isFullscreen,
    isFullscreenEnabled,
    toggleFullscreen,
    enterFullscreen,
    exitFullscreen,
  };
};
